import { Module } from 'vuex';
import { AiJobResponse, AiJobStatus, DecisionState } from '@/types/types';
import { getAiDecisionJob, getAiDecisionsForCase, startAiDecision } from '@/requests/aiDecisions';

/**
 * Job-Zustand der KI-Entscheidungsgenerierung, je Fall.
 *
 * Das Polling lebt im Store und nicht in der Komponente: wechselt die Mitarbeiterin während der
 * Generierung in eine andere Fallansicht und kommt zurück, muss der Fortschritt weiterlaufen.
 * Ein onUnmounted in der Komponente würde den Job aus ihrer Sicht verlieren, obwohl er
 * serverseitig fertig wird.
 *
 * Bewusst nicht persistiert (siehe PERSISTED_PATHS in store.ts) — ein Job-Zustand, der einen
 * Browser-Neustart überlebt, wäre garantiert veraltet. Achtung: ein eigener reducer in
 * vuex-persistedstate setzt diese Allowlist ausser Kraft, wenn er sie nicht selbst anwendet.
 * Genau dadurch hat eine Fehlermeldung frueher jeden Reload ueberlebt.
 */

const FAST_INTERVAL_MS = 1000;
const SLOW_INTERVAL_MS = 2500;
const FAST_PHASE_MS = 10000;

export interface AiDecisionState {
	jobs: { [caseId: string]: AiJobResponse };
	/** Fälle, in denen gerade ein Start-Request unterwegs ist — verhindert doppelte POSTs vor der Antwort. */
	starting: string[];
}

/** Timer liegen ausserhalb des States: sie sind nicht serialisierbar und gehoeren nicht in die Reaktivitaet. */
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const clearTimer = (caseId: string) => {
	const timer = timers.get(caseId);
	if (timer) {
		clearTimeout(timer);
		timers.delete(caseId);
	}
};

export const aiDecisionModule: Module<AiDecisionState, any> = {
	namespaced: true,
	state: (): AiDecisionState => ({
		jobs: {},
		starting: [],
	}),
	mutations: {
		setJob(state, job: AiJobResponse) {
			state.jobs = { ...state.jobs, [job.caseId]: job };
		},
		clearJob(state, caseId: string) {
			const jobs = { ...state.jobs };
			delete jobs[caseId];
			state.jobs = jobs;
		},
		setStarting(state, { caseId, value }: { caseId: string; value: boolean }) {
			state.starting = value ? [...new Set([...state.starting, caseId])] : state.starting.filter((id) => id !== caseId);
		},
	},
	actions: {
		/** Startet eine Generierung. Antwortet der Server mit 200 statt 202, laeuft bereits eine — dann haengen wir uns an. */
		async start({ commit, dispatch, state }, payload: { caseId: string; decision: DecisionState }) {
			if (state.starting.includes(payload.caseId)) return null;
			commit('setStarting', { caseId: payload.caseId, value: true });

			const result = await startAiDecision({ caseId: payload.caseId, decision: payload.decision });

			commit('setStarting', { caseId: payload.caseId, value: false });
			if (!result.success) return null;

			commit('setJob', result.data);
			dispatch('poll', payload.caseId);
			return result.data;
		},

		/** Haengt sich beim Laden der Fallansicht an einen serverseitig laufenden Job — deckt Reload, Navigation und fremd gestartete Laeufe ab. */
		async attach({ commit, dispatch, state }, caseId: string) {
			const result = await getAiDecisionsForCase(caseId);
			if (!result.success) return null;

			if (!result.data.runningJobId) {
				// Serverseitig laeuft nichts. Ein Fehlschlag im Store ist damit erledigt und darf die
				// Meldung nicht ueber die Ansicht hinaus konservieren. Ein erfolgreicher Job bleibt
				// liegen: den braucht der Watcher noch, um den Vorschlag zu uebernehmen.
				if (state.jobs[caseId]?.status === AiJobStatus.Failed) commit('clearJob', caseId);
				return null;
			}

			const job = await getAiDecisionJob(result.data.runningJobId);
			if (!job.success) return null;

			commit('setJob', job.data);
			if (job.data.status === AiJobStatus.Running) dispatch('poll', caseId);
			return job.data;
		},

		/**
		 * Verwirft eine Fehlermeldung, deren Ursache der Fall gerade behoben haben kann — etwa nach
		 * dem Erfassen einer Sujet-Beschreibung. Stehen zu lassen waere irrefuehrend, denn der
		 * genannte Mangel existiert dann nicht mehr.
		 */
		discardFailure({ commit, state }, caseId: string) {
			if (state.jobs[caseId]?.status === AiJobStatus.Failed) commit('clearJob', caseId);
		},

		/** Ein Tick des Pollings. Schnell in den ersten Sekunden, danach ruhiger. */
		async poll({ commit, dispatch, state }, caseId: string) {
			clearTimer(caseId);
			const current = state.jobs[caseId];
			if (!current || current.status !== AiJobStatus.Running) return;

			const result = await getAiDecisionJob(current.jobId);
			if (result.success) {
				commit('setJob', result.data);
				if (result.data.status !== AiJobStatus.Running) return;
			}

			const elapsed = Date.now() - new Date(current.startedAt).getTime();
			const interval = elapsed < FAST_PHASE_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
			timers.set(
				caseId,
				setTimeout(() => dispatch('poll', caseId), interval),
			);
		},

		stop(_context, caseId: string) {
			clearTimer(caseId);
		},
	},
	getters: {
		jobForCase: (state) => (caseId: string) => state.jobs[caseId] ?? null,
		isRunning: (state) => (caseId: string) =>
			state.jobs[caseId]?.status === AiJobStatus.Running || state.starting.includes(caseId),
	},
};
